import { activeBeat, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { colorForCharacterFromMap } from "../../character/colors";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
  timelineRailAxisY,
} from "../MarkerMetrics/metrics";
import { paintPortraitFaces, portraitMorphAt } from "../PortraitMorph/morph";
import { markerRailPose } from "../../rail/RailPose/pose";
import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";

const NAME_GAP_PX = 8;

/** DOM positions for character discs and names from rail pose (no chapter board). */
export function applyCharacterMarkerLayout(
  root: HTMLElement,
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
): void {
  const beat = activeBeat(layout, playheadMs);
  if (beat === undefined) {
    return;
  }
  const motion = computeSceneMotion(
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
  );
  const discRadius = MARKER_DIAMETER / 2;
  const axisY = timelineRailAxisY();
  const axisOffset = markerDiscAxisOffsetY();

  for (const marker of layout.markers) {
    const pose = markerRailPose(marker.characterId, playheadMs, layout.rail);
    const parts = root.querySelectorAll<HTMLElement>(
      `[data-character-id="${marker.characterId}"]`,
    );
    if (parts.length === 0) {
      continue;
    }
    if (pose === null) {
      hideParts(parts, 0);
      continue;
    }
    const centerX = motion.groupCenterX + pose.dx;
    const centerY = axisY + pose.railDy + pose.dy;
    for (const part of parts) {
      const below = part.dataset.markerPart === "name";
      const top = below
        ? centerY + discRadius + NAME_GAP_PX
        : centerY - discRadius - axisOffset;
      const fade = pose.fade ?? 1;
      const opacity = below ? pose.nameOpacity * fade : fade;
      placePart(part, centerX, top, opacity, pose.scale);
      if (!below) {
        part.style.zIndex = pose.behind ? "0" : "1";
      }
    }
    const disc = root.querySelector<HTMLElement>(
      `[data-marker-part="disc"][data-character-id="${marker.characterId}"] `
        + ".timeline-marker__disc",
    );
    if (disc !== null) {
      paintPortraitFaces(
        disc,
        portraitMorphAt(
          layout.rail.stays,
          marker.characterId,
          marker.imageUrl,
          marker.appearances,
          playheadMs,
        ),
        colorForCharacterFromMap(marker.characterId, layout.characterColors),
      );
    }
  }
}

function hideParts(parts: NodeListOf<HTMLElement>, opacity: number): void {
  for (const part of parts) {
    part.style.opacity = String(opacity);
    part.style.display = "none";
  }
}

function placePart(
  part: HTMLElement,
  centerX: number,
  top: number,
  opacity: number,
  scale: number,
): void {
  part.style.opacity = String(opacity);
  part.style.display = "";
  part.style.visibility = "visible";
  part.style.setProperty("--marker-diameter", `${MARKER_DIAMETER}px`);
  part.style.transform =
    `translate(${centerX}px, ${top}px) translate(-50%, 0) scale(${scale})`;
}
